import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import MoreVertOutlinedIcon from '@mui/icons-material/MoreVertOutlined';
import { IconButton, Switch, Tooltip } from '@mui/material';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import { PrimaryButton } from 'src/components/common/buttons/PrimaryButton';
import ContextMenu from 'src/components/common/context_menu/ContextMenu';
import { ConfirmationModal } from 'src/components/common/modal/ConfirmationModal';
import { snackbar } from 'src/components/common/snackbar/Snackbar';
import { DataTable } from 'src/components/common/table/DataTable';
import { buildQueryParams } from 'src/components/common/table/dataTableQueryBuilder';
import { IMenuItem } from 'src/components/common/types/contextMenuTypes';
import {
	IColumn,
	ITableQueryState,
} from 'src/components/common/types/tableTypes';
import {
	AI_MODEL_FILE_LOADING_KEY,
	getAIModelFiles,
	updateAIModelFile,
	deleteAIModelFile,
	IAIModelFileDetails,
	IAIModelFilesTableProps,
} from 'src/features/ai_model_file';
import { AppDispatch, RootState } from 'src/store';
import { formatTimestamp } from 'src/utils/dateUtils';

import UpdateAIModelFileModal from './UpdateAIModelFileModal';
import UploadAIModelFileModal from './UploadAIModelFileModal';

const DATA_TABLE_ROWS_PER_PAGE =
	Number(process.env.REACT_APP_DATA_TABLE_ROWS_PER_PAGE) || 10;

export const AIModelFilesTable: React.FC<IAIModelFilesTableProps> = ({
	aiModelId,
	canAddFiles,
	onFileCountChange,
}) => {
	const { siteSlug } = useParams<{ siteSlug: string }>();
	const dispatch = useDispatch<AppDispatch>();

	const isLoading = useSelector(
		(state: RootState) =>
			state.loading.loadingStates[AI_MODEL_FILE_LOADING_KEY],
	);

	const [totalCount, setTotalCount] = useState<number>(0);
	const [queryState, setQueryState] = useState<ITableQueryState>({ page: 0 });
	const [aiModelFiles, setAiModelFiles] = useState<IAIModelFileDetails[]>([]);
	const [isUploadModalOpen, setIsUploadModalOpen] = useState<boolean>(false);
	const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
	const [selectedRow, setSelectedRow] = useState<IAIModelFileDetails | null>(
		null,
	);
	const [anchorElement, setAnchorElement] = useState<null | HTMLElement>(
		null,
	);

	const menuItems: IMenuItem[] = [
		{
			label: 'Edit',
			icon: <EditOutlinedIcon />,
			onClick: () => {
				if (selectedRow) {
					setIsEditModalOpen(true);
				}
				setAnchorElement(null);
			},
		},
		{
			label: 'Download',
			icon: <DownloadOutlinedIcon />,
			onClick: () => {
				if (selectedRow) {
					handleDownload(selectedRow);
				}
				setAnchorElement(null);
			},
		},
		{
			label: 'Delete',
			icon: <DeleteOutlineOutlinedIcon color='error' />,
			onClick: () => {
				if (selectedRow) {
					setIsDeleteModalOpen(true);
				}
				setAnchorElement(null);
			},
			color: 'error.main',
		},
	];

	const patchAIModelFileById = (
		fileId: number,
		patch: Partial<IAIModelFileDetails>,
	) => {
		setAiModelFiles((prev) =>
			prev.map((f) => (f.id === fileId ? { ...f, ...patch } : f)),
		);
	};

	const handleMenuClick = (
		event: React.MouseEvent<HTMLElement>,
		data: IAIModelFileDetails,
	) => {
		setSelectedRow(data);
		setAnchorElement(event.currentTarget);
	};

	const handleDownload = (file: IAIModelFileDetails) => {
		try {
			if (!file.file_url) {
				throw new Error('File URL not available');
			}

			const link = document.createElement('a');
			link.href = file.file_url;
			link.download = file.name || 'download';
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
		} catch {
			snackbar('Failed to download file', 'error');
		}
	};

	const handleDelete = () => {
		if (!siteSlug || !selectedRow) return;
		dispatch(
			deleteAIModelFile({
				siteSlug,
				fileId: selectedRow.id,
				onSuccess: () => {
					snackbar('File deleted successfully', 'success');
					setIsDeleteModalOpen(false);
					fetchAIModelFiles();
				},
			}),
		);
	};

	const handleActiveToggle = (file: IAIModelFileDetails) => {
		if (!siteSlug) return;

		const nextActive = !file.active;
		patchAIModelFileById(file.id, { active: nextActive });

		dispatch(
			updateAIModelFile({
				siteSlug,
				fileId: file.id,
				request: {
					active: nextActive,
				},
				onSuccess: (updated) => {
					patchAIModelFileById(file.id, updated);
					snackbar(
						`File ${nextActive ? 'activated' : 'deactivated'} successfully`,
						'success',
					);
				},
				onError: () => {
					patchAIModelFileById(file.id, { active: file.active });
				},
			}),
		);
	};

	const columns: IColumn<IAIModelFileDetails>[] = [
		{
			key: 'sn',
			label: 'S.N.',
			width: '60px',
			render: (_data, index) => {
				const page = queryState.page || 0;
				return page * DATA_TABLE_ROWS_PER_PAGE + index + 1;
			},
		},
		{
			key: 'name',
			label: 'Name',
			render: (data) => (
				<Typography
					variant='body2'
					sx={{
						overflowWrap: 'break-word',
						textOverflow: 'ellipsis',
					}}
				>
					{data.name}
				</Typography>
			),
		},
		{
			key: 'description',
			label: 'Description',
			render: (data) => (
				<Typography
					variant='body2'
					sx={{
						overflowWrap: 'break-word',
						textOverflow: 'ellipsis',
					}}
				>
					{data.description || '-'}
				</Typography>
			),
		},
		{
			key: 'sha_256',
			label: 'SHA-256',
			render: (data) => (
				<Typography
					variant='body2'
					sx={{
						fontFamily: 'monospace',
						wordBreak: 'break-all',
						fontSize: '0.75rem',
						backgroundColor: '#f5f5f5',
						padding: '4px 8px',
						borderRadius: '4px',
						display: 'inline-block',
					}}
				>
					{data.sha_256}
				</Typography>
			),
		},
		{
			key: 'active',
			label: 'Active',
			filterable: true,
			filterParamKey: 'active',
			menuItems: [
				{ label: 'Yes', value: 'true' },
				{ label: 'No', value: 'false' },
			],
			render: (data) => (
				<Box sx={{ display: 'flex', alignItems: 'center' }}>
					<Switch
						checked={data.active}
						onChange={() => handleActiveToggle(data)}
						disabled={isLoading}
						size='small'
					/>
				</Box>
			),
		},
		{
			key: 'created_at',
			label: 'Created At',
			sortable: true,
			render: (data) => formatTimestamp(data.created_at),
		},
		{
			key: 'actions',
			label: 'Actions',
			align: 'left',
			render: (data) => (
				<IconButton
					onClick={(e) => {
						handleMenuClick(e, data);
					}}
					disabled={isLoading}
				>
					<MoreVertOutlinedIcon />
				</IconButton>
			),
		},
	];

	const fetchAIModelFiles = () => {
		if (!siteSlug) return;
		const params = buildQueryParams(queryState);

		dispatch(
			getAIModelFiles({
				aiModelId,
				params,
				siteSlug: siteSlug,
				onSuccess: (data) => {
					setTotalCount(data.info.count);
					setAiModelFiles(data.results);
					onFileCountChange(data.info.count);
				},
			}),
		);
	};

	useEffect(() => {
		fetchAIModelFiles();
	}, [queryState, siteSlug, aiModelId]);

	return (
		<Box
			sx={{
				display: 'flex',
				flexDirection: 'column',
				backgroundColor: 'white',
				borderRadius: '4px',
				padding: 3,
			}}
		>
			<Box
				sx={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					mb: 3,
					flexShrink: 0,
				}}
			>
				<Typography
					variant='h6'
					sx={{
						fontSize: '20px',
						fontWeight: 500,
						color: 'rgba(0, 0, 0, 0.87)',
					}}
				>
					AI Model Files
				</Typography>
				<Box sx={{ width: '231px', height: '36px' }}>
					<Tooltip
						title={
							!canAddFiles
								? 'Add class labels first before adding AI model files'
								: ''
						}
						arrow
						disableHoverListener={canAddFiles}
					>
						<span>
							<PrimaryButton
								variant='contained'
								color='primary'
								size='medium'
								onClick={() => setIsUploadModalOpen(true)}
								disabled={isLoading || !canAddFiles}
							>
								UPLOAD FILE
							</PrimaryButton>
						</span>
					</Tooltip>
				</Box>
			</Box>

			<Box>
				<DataTable
					columns={columns}
					data={aiModelFiles}
					loadingKey={AI_MODEL_FILE_LOADING_KEY}
					emptyMessage='No AI model files found'
					paginationInfo={{
						totalCount,
					}}
					queryState={queryState}
					onQueryChange={setQueryState}
				/>
			</Box>

			<ContextMenu
				open={Boolean(anchorElement)}
				onClose={() => setAnchorElement(null)}
				anchorElement={anchorElement}
				clearAnchorElement={() => setAnchorElement(null)}
				items={menuItems}
			/>

			<UploadAIModelFileModal
				isOpen={isUploadModalOpen}
				aiModelId={aiModelId}
				onClose={() => setIsUploadModalOpen(false)}
				onSuccess={() => {
					setQueryState({ page: 0 });
				}}
			/>

			{selectedRow && (
				<>
					<UpdateAIModelFileModal
						initialValues={{
							name: selectedRow.name,
							description: selectedRow.description || '',
						}}
						isOpen={isEditModalOpen}
						fileId={selectedRow.id}
						onClose={() => setIsEditModalOpen(false)}
						onSuccess={(updated) => {
							patchAIModelFileById(updated.id, {
								name: updated.name,
								description: updated.description,
							});
						}}
					/>

					<ConfirmationModal
						modalOpen={isDeleteModalOpen}
						handleModalCLose={() => setIsDeleteModalOpen(false)}
						title='Delete AI Model File'
						modalContent={`Are you sure you want to delete "${selectedRow.name}"?`}
						handleYes={handleDelete}
						handleNo={() => setIsDeleteModalOpen(false)}
						loadingKey={AI_MODEL_FILE_LOADING_KEY}
						modalYesBtnTitle='DELETE'
					/>
				</>
			)}
		</Box>
	);
};

export default AIModelFilesTable;
